if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        // Recopilar todas las properties del producto principal
        const mainItem = {
          id: formData.get('id'),
          quantity: 1,
          properties: {}
        };

        for (let [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propName = key.match(/properties\[([^\]]+)\]/)[1];
            mainItem.properties[propName] = value;
          }
        }

        // Verificar si hay distrito seleccionado
        if (window.selectedShipping && window.selectedShipping.costo) {
          // Agregar el costo como property oculta (prefijo _ para que no se muestre en checkout)
          mainItem.properties['_Costo de envío'] = 'S/ ' + window.selectedShipping.costo.toFixed(2);
          
          // Primero buscar el producto de envío por handle y luego la variante por SKU
          this.addProductWithShipping(mainItem, window.selectedShipping.costo, config);
        } else {
          // Si no hay distrito, solo agregar el producto principal
          this.addSingleProduct(mainItem, config);
        }
      }

      addProductWithShipping(mainItem, shippingCost, config) {
        // Generar un ID único para vincular producto padre con su envío
        const groupId = Date.now().toString(36) + Math.random().toString(36).substr(2, 6);
        mainItem.properties['_group_id'] = groupId;

        // Usar la variante pre-resuelta desde el cache (0ms de lookup)
        const shippingVariantId = window.selectedShipping?.variantId;

        if (!shippingVariantId) {
          // Fallback: si el cache no está listo, buscar en tiempo real
          console.warn('Cache de envío no disponible, buscando en tiempo real...');
          this.addProductWithShippingFallback(mainItem, shippingCost, groupId, config);
          return;
        }

        const shippingItem = {
          id: shippingVariantId,
          quantity: 1,
          properties: {
            '_type': 'shipping',
            '_group_id': groupId,
            '_distrito': window.selectedShipping.nombre,
            '_zona': window.selectedShipping.zona
          }
        };

        // Single call: ambos items en un solo request + section rendering
        // shippingItem primero para que mainItem (último) aparezca primero en cart (LIFO)
        this.submitToCart([shippingItem, mainItem], config, mainItem.id);
      }

      addProductWithShippingFallback(mainItem, shippingCost, groupId, config) {
        // Fallback: buscar variante en tiempo real si el cache no estaba listo
        fetch('/products/costo-de-envio.js')
          .then(response => response.json())
          .then(product => {
            window.shippingVariantsCache = product.variants;

            const shippingVariant = product.variants.find(v => (v.price / 100) === shippingCost);

            if (!shippingVariant) {
              console.warn('No se encontró variante de envío para S/ ' + shippingCost);
              this.addSingleProduct(mainItem, config);
              return;
            }

            if (window.selectedShipping) window.selectedShipping.variantId = shippingVariant.id;

            const shippingItem = {
              id: shippingVariant.id,
              quantity: 1,
              properties: {
                '_type': 'shipping',
                '_group_id': groupId,
                '_distrito': window.selectedShipping.nombre,
                '_zona': window.selectedShipping.zona
              }
            };

            // Single call: ambos items en un solo request
            this.submitToCart([shippingItem, mainItem], config, mainItem.id);
          })
          .catch(error => {
            console.error('Error buscando producto de envío:', error);
            this.addSingleProduct(mainItem, config);
          });
      }

      addSingleProduct(mainItem, config) {
        this.submitToCart([mainItem], config, mainItem.id);
      }

      submitToCart(items, config, productVariantId) {
        const addToCartData = { items: items };

        if (this.cart) {
          addToCartData.sections = this.cart.getSectionsToRender().map((section) => section.id);
          addToCartData.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = JSON.stringify(addToCartData);
        config.headers['Content-Type'] = 'application/json';

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: productVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: productVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
